import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import {
  Package,
  Plus,
  Search,
  Filter,
  Trash2,
  Edit,
  Calendar,
  Loader2,
  AlertCircle
} from "lucide-react";

const CATEGORIES = [
  "antibiotique", "antalgique", "antidiabétique", "antihypertenseur",
  "antipaludéen", "vitamine", "antiseptique", "dermatologique",
  "gastro-entérologie", "respiratoire", "autre"
];

const ProductsPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: "",
    category: "",
    batch_number: "",
    expiry_date: "",
    quantity: "",
    unit_price: "",
    unit_weight: "",
    supplier: ""
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [products, searchTerm, categoryFilter, statusFilter]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des produits");
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = [...products];
    
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.batch_number.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (categoryFilter !== "all") {
      filtered = filtered.filter(p => p.category === categoryFilter);
    }
    
    if (statusFilter !== "all") {
      filtered = filtered.filter(p => p.status === statusFilter);
    }
    
    setFilteredProducts(filtered);
  };

  const handleAddProduct = async () => {
    setAddLoading(true);
    try {
      const payload = {
        ...newProduct,
        quantity: parseInt(newProduct.quantity),
        unit_price: parseFloat(newProduct.unit_price),
        unit_weight: newProduct.unit_weight ? parseFloat(newProduct.unit_weight) : null
      };
      await axios.post(`${API}/products`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Produit ajouté avec succès");
      setShowAddModal(false);
      setNewProduct({
        name: "", category: "", batch_number: "",
        expiry_date: "", quantity: "", unit_price: "", unit_weight: "", supplier: ""
      });
      fetchProducts();
    } catch (error) {
      toast.error("Erreur lors de l'ajout du produit");
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce produit?")) return;
    
    try {
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Produit supprimé");
      fetchProducts();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-CI', { 
      style: 'currency', 
      currency: 'XOF',
      maximumFractionDigits: 0 
    }).format(value);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      pending_destruction: "bg-amber-500/100/20 text-amber-400 border-amber-500/30",
      destroyed: "bg-red-500/100/20 text-red-400 border-red-500/30"
    };
    const labels = {
      active: "Actif",
      pending_destruction: "Destruction en cours",
      destroyed: "Détruit"
    };
    return (
      <Badge variant="outline" className={styles[status] || styles.active}>
        {labels[status] || status}
      </Badge>
    );
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6" data-testid="products-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-outfit font-bold text-white">
              Gestion des Produits
            </h1>
            <p className="text-slate-400 mt-1">
              {products.length} produit(s) dans l'inventaire
            </p>
          </div>
          
          <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-product-btn"
                className="btn-primary gap-2"
              >
                <Plus className="w-5 h-5" />
                Ajouter un Produit
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-white/10 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-white font-outfit">Nouveau Produit</DialogTitle>
                <DialogDescription className="text-slate-400">
                  Ajoutez un nouveau produit à votre inventaire
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Nom du produit</Label>
                    <Input
                      value={newProduct.name}
                      onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                      placeholder="Paracétamol 500mg"
                      data-testid="product-name-input"
                      className="bg-slate-950/50 border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Catégorie</Label>
                    <Select
                      value={newProduct.category}
                      onValueChange={(v) => setNewProduct({...newProduct, category: v})}
                    >
                      <SelectTrigger className="bg-slate-950/50 border-white/10 text-white">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-white/10">
                        {CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat} className="text-slate-300">
                            {cat.charAt(0).toUpperCase() + cat.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">N° de Lot</Label>
                    <Input
                      value={newProduct.batch_number}
                      onChange={(e) => setNewProduct({...newProduct, batch_number: e.target.value})}
                      placeholder="LOT-2024-001"
                      className="bg-slate-950/50 border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Date d'expiration</Label>
                    <Input
                      type="date"
                      value={newProduct.expiry_date}
                      onChange={(e) => setNewProduct({...newProduct, expiry_date: e.target.value})}
                      className="bg-slate-950/50 border-white/10 text-white"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Quantite</Label>
                    <Input
                      type="number"
                      value={newProduct.quantity}
                      onChange={(e) => setNewProduct({...newProduct, quantity: e.target.value})}
                      placeholder="100"
                      className="bg-slate-950/50 border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Prix unitaire (FCFA)</Label>
                    <Input
                      type="number"
                      value={newProduct.unit_price}
                      onChange={(e) => setNewProduct({...newProduct, unit_price: e.target.value})}
                      placeholder="2500"
                      className="bg-slate-950/50 border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Poids unitaire (kg)</Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={newProduct.unit_weight}
                      onChange={(e) => setNewProduct({...newProduct, unit_weight: e.target.value})}
                      placeholder="0.05"
                      data-testid="product-weight-input"
                      className="bg-slate-950/50 border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Fournisseur</Label>
                    <Input
                      value={newProduct.supplier}
                      onChange={(e) => setNewProduct({...newProduct, supplier: e.target.value})}
                      placeholder="Pharma CI"
                      className="bg-slate-950/50 border-white/10 text-white"
                    />
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button 
                  variant="ghost" 
                  onClick={() => setShowAddModal(false)}
                  className="text-slate-400"
                >
                  Annuler
                </Button>
                <Button 
                  onClick={handleAddProduct}
                  disabled={addLoading || !newProduct.name || !newProduct.quantity}
                  data-testid="confirm-add-product-btn"
                  className="btn-primary"
                >
                  {addLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ajouter"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Rechercher par nom ou N° de lot..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-products-input"
                  className="pl-10 bg-slate-950/50 border-white/10 text-white"
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full md:w-48 bg-slate-950/50 border-white/10 text-white">
                  <Filter className="w-4 h-4 mr-2 text-slate-500" />
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  <SelectItem value="all" className="text-slate-300">Toutes catégories</SelectItem>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat} className="text-slate-300">
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-40 bg-slate-950/50 border-white/10 text-white">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  <SelectItem value="all" className="text-slate-300">Tous statuts</SelectItem>
                  <SelectItem value="active" className="text-slate-300">Actif</SelectItem>
                  <SelectItem value="pending_destruction" className="text-slate-300">Destruction en cours</SelectItem>
                  <SelectItem value="destroyed" className="text-slate-300">Détruit</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Products Table */}
        <Card className="glass-card">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
              </div>
            ) : filteredProducts.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr className="text-left border-b border-white/10">
                      <th className="p-4 text-slate-400 font-medium text-sm">Produit</th>
                      <th className="p-4 text-slate-400 font-medium text-sm">Categorie</th>
                      <th className="p-4 text-slate-400 font-medium text-sm">N Lot</th>
                      <th className="p-4 text-slate-400 font-medium text-sm">Expiration</th>
                      <th className="p-4 text-slate-400 font-medium text-sm">Qte</th>
                      <th className="p-4 text-slate-400 font-medium text-sm">Poids (kg)</th>
                      <th className="p-4 text-slate-400 font-medium text-sm">Prix</th>
                      <th className="p-4 text-slate-400 font-medium text-sm">Statut</th>
                      <th className="p-4 text-slate-400 font-medium text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map((product) => (
                      <tr key={product.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-sky-500/10 rounded-lg">
                              <Package className="w-4 h-4 text-sky-400" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{product.name}</p>
                              <p className="text-slate-500 text-xs">{product.supplier}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-slate-300 capitalize">{product.category}</td>
                        <td className="p-4 text-slate-400 font-mono text-sm">{product.batch_number}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-2 text-slate-300">
                            <Calendar className="w-4 h-4 text-slate-500" />
                            {product.expiry_date}
                          </div>
                        </td>
                        <td className="p-4 text-white font-medium">{product.quantity}</td>
                        <td className="p-4 text-slate-400 text-sm">{product.unit_weight ? `${(product.quantity * product.unit_weight).toFixed(2)}` : "-"}</td>
                        <td className="p-4 text-amber-400">{formatCurrency(product.unit_price)}</td>
                        <td className="p-4">{getStatusBadge(product.status)}</td>
                        <td className="p-4">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteProduct(product.id)}
                            disabled={product.status !== 'active'}
                            data-testid={`delete-product-${product.id}`}
                            className="text-slate-400 hover:text-red-400 disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <Package className="w-12 h-12 mb-4 opacity-50" />
                <p className="text-lg">Aucun produit trouvé</p>
                <p className="text-sm">Ajoutez des produits ou importez un fichier CSV</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ProductsPage;
